"use client"

import dynamic from "next/dynamic"
import Image from "next/image"
import { useCallback, useEffect, useState, createContext, useContext, type MouseEvent } from "react"
import { Check } from "lucide-react"

import {
  CONFIDENCE_SCORE_CAVEAT,
  getConfidenceLabel,
  getConfidenceBarColor,
  getConfidenceLabelColor,
  isHighConfidence as isHighConfidenceScore,
  isLowConfidence as isLowConfidenceScore,
} from "@/lib/confidence-display"
import { useHomepageAnalysis, type InitialSavedAnalysis } from "@/hooks/use-homepage-analysis"
import { analysisFunnelEnvelope, trackEvent } from "@/lib/analytics"
import { SavedResultWeekStrip } from "@/components/home/saved-result-week-strip"
import type { WeekStripData } from "@/lib/saved-result-week-strip"
import type { WeatherData } from "@/lib/weather-utils"
import { ResultAttributionTracker } from "./result-attribution"
import { DeleteButton } from "./DeleteButton"
import { useWeedIdCapability } from "@/hooks/use-weed-id-capability"
import { buildAnalysisRescanUrl } from "@/lib/analysis/rescan-url"
import type { RefinementTrigger } from "@/lib/analysis/refinement-policy"
import {
  isModifiedNavigationClick,
  navigateToResolvedUrl,
  openModifiedClickTarget,
} from "@/lib/navigation/modified-click"

// Static result hero that paints IMMEDIATELY on cold inbound share-arrivals,
// before the heavy interactive result chunk loads. Built entirely from the
// server-fetched row (`initial`) so the user sees the real grass name, their
// own photo, and the confidence number on first paint — no blank zone, no
// generic gray box. The full interactive surface (care-plan accordions,
// sign-in, products, tools) swaps in below once its JS arrives. The skeleton
// mirrors the high-confidence header + photo + confidence bar of
// `ResultsDisplay` closely enough that the swap is near-seamless.
//
// These heroes are the `loading` slot for the dynamic components below.
// `dynamic()`'s loading callback receives no props, so the fallbacks read the
// server-fetched `initial` row from `ResultInitialContext` (provided in the
// component's render). The dynamic imports MUST stay at module scope — an
// earlier version created them per-render inside `useMemo`, which defeated
// Next's static chunk preload. On slow clients the chunk then lost the
// hydration race and the loading hero painted where the server had rendered
// the real component, tripping a React #418 element-level hydration mismatch
// (mobile-only, intermittent). Module scope lets the chunk preload so the
// client hydrates the real component and the fallback effectively never shows.

function ResultIdentifyHero({ initial }: { initial: InitialSavedAnalysis }) {
  const result = initial.result
  const grassType = result?.grassType ?? "Your lawn"
  // Server-gated display confidence when present (honesty parity with the
  // live results surface); raw as fallback for pre-gating rows.
  const confidence =
    typeof result?.displayConfidence === "number"
      ? result.displayConfidence
      : typeof result?.confidence === "number"
        ? result.confidence
        : 0
  const region = result?.region || ""
  const isUserSelectedIdentity = result?.identitySource === "user_selected"
  const isHighConfidence = isHighConfidenceScore(confidence)
  const photo = initial.imageUrl || result?.image || ""

  return (
    <section className="relative px-3 pt-3 pb-6 md:px-10 md:py-8 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white border border-gray-100 shadow-xs rounded-xl">
          <div className="p-3 md:p-10 lg:p-12">
            <div className="text-center mb-5 md:mb-8">
              {isUserSelectedIdentity ? (
                <>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-blue-700 mb-3 md:mb-4">
                    <Check className="w-3 h-3" strokeWidth={3} />
                    You selected this match
                  </div>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
                    Using{" "}
                    <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
                      {grassType}.
                    </span>
                  </h1>
                  <p className="mt-3 md:mt-5 mx-auto max-w-2xl text-sm md:text-base lg:text-lg text-gray-500 leading-relaxed">
                    Your selection replaces the original guess. The model score was not reassigned to it.
                  </p>
                  {/* Mirrors the same history line in ResultsDisplay. The shell
                      is what paints before the live component swaps in, so a
                      line missing here is a layout shift there. */}
                  {result?.originalGrassType && (
                    <p className="mt-2 mx-auto max-w-2xl text-[12.5px] text-gray-400 leading-snug">
                      Our model said {result.originalGrassType}, {result.originalConfidence ?? confidence}%.
                    </p>
                  )}
                </>
              ) : isHighConfidence ? (
                <>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-green-700 mb-3 md:mb-4">
                    <Check className="w-3 h-3" strokeWidth={3} />
                    Match found
                  </div>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
                    That&apos;s{" "}
                    <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
                      {grassType}.
                    </span>
                  </h1>
                  {region && (
                    <p className="mt-3 md:mt-5 mx-auto max-w-2xl text-sm md:text-base lg:text-lg text-gray-500 leading-relaxed">
                      Common across the {region}.
                      {confidence > 0 && <> We&apos;re {confidence}% sure on this ID.</>}
                      {confidence > 0 && (
                        <span className="mt-1 block text-[11.5px] text-gray-400">
                          {CONFIDENCE_SCORE_CAVEAT}
                        </span>
                      )}
                    </p>
                  )}
                </>
              ) : (
                // Sub-70% (regional best guess): calmer framing so a shared /
                // cold-painted low-confidence result doesn't read as a confirmed
                // match (matches the live ResultsDisplay tone).
                <>
                  <h1 className="text-xl md:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 leading-tight">
                    Likely {grassType}
                  </h1>
                  <p className="mt-2 md:mt-4 mx-auto max-w-2xl text-sm md:text-base text-gray-500 leading-relaxed">
                    Our best guess{region ? ` for the ${region}` : ""}
                    {confidence > 0 ? `, with ${confidence}% confidence` : ""}. A clearer
                    close-up of the blades can confirm it.
                    {confidence > 0 && (
                      <span className="mt-1 block text-[11.5px] text-gray-400">
                        {CONFIDENCE_SCORE_CAVEAT}
                      </span>
                    )}
                  </p>
                </>
              )}
              {/* CTA slot reservation (CLS audit 2026-07-30): the real
                  ResultsDisplay renders a hero CTA (~48px button, mt-4/5) here
                  for every savable result once its chunk + auth state land.
                  The hero must hold that space or everything below the H1
                  shifts down at swap time — the top field-CLS contributor on
                  /result/*. Unknown/Other results never get a CTA, so they
                  skip the reservation. */}
              {grassType !== "Unknown/Other" && (
                <div className="mt-4 md:mt-5">
                  <div className="mx-auto h-12 w-full sm:w-72 rounded-xl bg-gray-100/80 animate-pulse" />
                </div>
              )}
            </div>

            <div className="grid gap-6 md:gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12 items-start min-w-0">
              <div className="min-w-0">
                <div className="flex flex-col items-center text-center md:block md:text-center lg:text-left">
                  <div className="relative flex-shrink-0 mb-4 md:mb-6 w-full">
                    <div className="relative w-40 h-40 mx-auto md:w-full md:h-auto md:aspect-square lg:aspect-[4/3] bg-linear-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-lg">
                      {photo && (
                        <Image
                          src={photo}
                          alt="Your grass photo"
                          fill
                          priority
                          sizes="(max-width: 768px) 160px, (max-width: 1024px) 50vw, 33vw"
                          className="object-cover"
                        />
                      )}
                    </div>
                  </div>

                  <div className="w-full min-w-0">
                    <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 mb-3 leading-tight">
                      {grassType}
                    </h2>
                    {confidence > 0 && !isUserSelectedIdentity && (
                      <div
                        className={`rounded-xl border p-3 md:p-4 text-left ${
                          isLowConfidenceScore(confidence)
                            ? "border-gray-100 bg-gray-50/60"
                            : "border-green-100 bg-green-50/60"
                        }`}
                      >
                        <div className="flex justify-between items-center text-sm mb-2">
                          <span className={`font-semibold ${getConfidenceLabelColor(confidence)}`}>
                            {getConfidenceLabel(confidence)}
                          </span>
                          <span className="text-gray-500 font-medium tabular-nums">
                            {confidence}%
                          </span>
                        </div>
                        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getConfidenceBarColor(confidence)}`}
                            style={{ width: `${confidence}%` }}
                          />
                        </div>
                        {/* Same string the live ConfidenceMeter renders, so the
                            shell and the swapped-in component do not disagree
                            about what the number promises. */}
                        <p className="mt-2 text-[11px] leading-snug text-gray-500">
                          {CONFIDENCE_SCORE_CAVEAT}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right column placeholder — the interactive plan/sign-in card
                  fills this once the chunk loads. A quiet shimmer keeps the
                  frame balanced without faking content we don't have. */}
              <div className="hidden lg:block min-w-0">
                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 h-64 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ResultDiagnosisHero({ initial }: { initial: InitialSavedAnalysis }) {
  const problemName =
    (initial.diagnosis?.problems?.[0]?.name as string | undefined) ?? "Lawn diagnosis"
  const photo = initial.imageUrl || (initial.diagnosis?.image as string | undefined) || ""

  return (
    <section className="relative px-3 py-6 md:px-10 md:py-8 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white border border-green-100/70 shadow-xs rounded-3xl p-3 md:p-10 lg:p-12">
          {/* Verdict-first order to match the hydrated DiagnosisResults hero
              (eyebrow → problem name → photo), so the loading→real swap doesn't
              reflow. This fallback effectively never paints (the real component
              is SSR'd + its chunk preloads), but keeping the order identical
              avoids an element-level hydration mismatch on slow clients. */}
          <div className="text-center mb-5 md:mb-8">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700 mb-3 md:mb-4">
              Diagnosis
            </div>
            <h1 className="text-[1.75rem] leading-[1.08] md:text-4xl lg:text-[2.75rem] lg:leading-[1.05] font-extrabold tracking-[-0.02em] text-gray-900">
              {problemName}
            </h1>
          </div>
          <div className="grid gap-6 md:gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12 items-start min-w-0">
            <div className="min-w-0">
              <div className="relative w-full h-44 md:h-auto md:aspect-square lg:aspect-[4/3] rounded-2xl overflow-hidden shadow-lg bg-linear-to-br from-gray-100 to-gray-200">
                {photo && (
                  <Image
                    src={photo}
                    alt="Your lawn photo"
                    fill
                    priority
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover"
                  />
                )}
              </div>
            </div>
            <div className="hidden lg:block min-w-0">
              <div className="rounded-2xl border border-gray-100 bg-gray-50/60 h-64 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// Context lets the module-scope `loading` fallbacks reach `initial` (the
// server-fetched row) to paint a fully-populated cold hero without threading
// props through `dynamic()` (whose loading callback takes none). The Provider
// wraps the render below.
const ResultInitialContext = createContext<InitialSavedAnalysis | null>(null)

function IdentifyLoadingHero() {
  const initial = useContext(ResultInitialContext)
  return initial ? <ResultIdentifyHero initial={initial} /> : null
}

function DiagnosisLoadingHero() {
  const initial = useContext(ResultInitialContext)
  return initial ? <ResultDiagnosisHero initial={initial} /> : null
}

// Module scope so the chunks preload and there is no hydration race (see the
// long comment above ResultIdentifyHero). `dynamic()` defaults to `ssr: true`,
// so the real components stay server-rendered — full content in SSR HTML for
// featured/indexed result pages.
const ResultsDisplay = dynamic(
  () => import("@/components/home/results-display").then((mod) => ({ default: mod.ResultsDisplay })),
  { loading: IdentifyLoadingHero },
)

const DiagnosisResults = dynamic(
  () => import("@/components/home/diagnosis-results").then((mod) => ({ default: mod.DiagnosisResults })),
  { loading: DiagnosisLoadingHero },
)
const WeedResultPresentation = dynamic(
  () => import("@/components/weed-identifier/weed-result-presentation").then((mod) => ({ default: mod.WeedResultPresentation })),
  { loading: IdentifyLoadingHero },
)

export interface SavedResultPageContentProps {
  initial: InitialSavedAnalysis
  resultId: string
  canDelete: boolean
  /**
   * Whether the weed-recovery API would authorize *this* viewer for this row,
   * evaluated server-side with the same predicate the route uses. This page is
   * a bearer share link, so a URL holder who is not the creator must not be
   * offered a CTA that can only 403.
   */
  canRecoverWeed: boolean
  editorNote?: string | null
  isFeatured: boolean
  /** WS6 seasonal ribbon, derived server-side (null hides it entirely). */
  weekStrip?: WeekStripData | null
  /**
   * Week-ahead forecast, also derived server-side. This route withholds the
   * exact ZIP from the client, so the digest cannot fetch a forecast itself;
   * the server hands it the finished numbers plus a region label instead.
   */
  weekAhead?: WeatherData | null
  weekAheadLocationLabel?: string
  /**
   * Days until the 30-day anon-row cleanup deletes this result, or null when
   * the row is owned/verified and never expires. Feeds the return-visit line's
   * honest deadline (godaddy-checkout rule: real deadlines only).
   */
  anonExpiresInDays?: number | null
  /**
   * True when the row has no owning account. The return-visit treatment says
   * "your plan is still unsaved", which is only true for unowned rows — a
   * bearer-link viewer of someone else's saved result must not see it.
   */
  isAnonRow?: boolean
}

/**
 * Per-result visit counter for the returning non-saver treatment.
 * localStorage so a shared-link FIRST-time viewer is never greeted with
 * "welcome back" — the count is this browser's, not the row's. Mirrors the
 * time-stamped shape of use-last-analysis-breadcrumb but with no TTL: a return
 * a week later is exactly the cohort we want (350 users/30d re-open results
 * 1h+ later without saving). Count is read once on mount, then incremented,
 * so the value is stable for the session and starts at 1 on the first visit.
 */
function useResultVisitCount(resultId: string): number {
  const [visitCount, setVisitCount] = useState(1)
  useEffect(() => {
    try {
      const key = `resultVisitCount_${resultId}`
      const prior = Number.parseInt(window.localStorage.getItem(key) ?? "0", 10)
      // Cap BEFORE both the write and the state set so analytics/pluralization
      // never diverge from storage.
      const count = Math.min((Number.isFinite(prior) && prior >= 0 ? prior : 0) + 1, 99)
      window.localStorage.setItem(key, String(count))
      setVisitCount(count)
    } catch {
      // Storage unavailable (private mode): stay at 1 = treatment suppressed.
    }
  }, [resultId])
  return visitCount
}

export function SavedResultPageContent({
  initial,
  resultId,
  canDelete,
  canRecoverWeed,
  editorNote,
  isFeatured,
  weekStrip,
  weekAhead,
  weekAheadLocationLabel,
  anonExpiresInDays,
  isAnonRow,
}: SavedResultPageContentProps) {
  const visitCount = useResultVisitCount(resultId)
  const { capability: weedCapability } = useWeedIdCapability()
  // Both gates must pass: the rollout flag decides whether the feature exists,
  // server-side ownership decides whether this viewer can actually use it.
  const weedRecoveryAvailable = weedCapability.recoveryEnabled && canRecoverWeed
  const {
    uploadedImage,
    uploadedImageUrl,
    analysisResult,
    diagnosisResult,
    zipCode,
    savedAnalysisId,
    recoveredWeed,
    isRecoveringWeed,
    weedRecoveryFailed,
    funnelId,
    attemptId,
    originLawnId,
    selectedLawnId,
    carePlanEmail,
    carePlanEmailError,
    emailSuggestion,
    isSubmittingCarePlan,
    emailGateCompleted,
    completedTimelineUrl,
    timelineRedirectUrl,
    oauthResumeUrl,
    navigateToCompletedTimeline,
    returnedFromGuide,
    dismissReturnedFromGuide,
    user,
    authLoading,
    handleCarePlanEmailChange,
    handleCarePlanEmailKeyPress,
    handleAcceptEmailSuggestion,
    handleDismissEmailSuggestion,
    handleCarePlanSubmit,
    handleSaveAndViewTimeline,
    selectAlternative,
    clearDiagnosisResult,
    persistDiagnosisForOAuth,
    recoverIdentifyAsWeed,
    setAnalysisMode,
    scanUsage,
    authIsPremium,
    subscriptionLoading,
  } = useHomepageAnalysis({
    initialSavedAnalysis: initial,
    oauthResumePath: `/result/${resultId}`,
  })

  // "View 12-month timeline" for already-saved analyses. Mirrors the inline
  // implementation in `app/page-content.tsx` but uses the savedAnalysisId
  // from the row (always present here) so the timeline gets the analysisId
  // param without an extra save round trip.
  const handleViewTimeline = useCallback(() => {
    trackEvent("care_plan_cta_clicked", {
      cta: "view_timeline",
      entry_path: "saved_result",
      grass_type: analysisResult?.grassType,
      zip_prefix: zipCode ? zipCode.slice(0, 3) : undefined,
      analysis_id: savedAnalysisId ?? undefined,
    })
  }, [analysisResult?.grassType, savedAnalysisId, zipCode])

  // "Try a different photo" on the saved-result page sends the user back to
  // the homepage to upload a new image (no reason to clear in-place — there's
  // no upload form on this page).
  // The `#analysis-card` hash is what makes this land on the uploader: without
  // it the fresh document opens at scroll 0 on the hero, with the upload form
  // below the fold on a phone.
  const handleResetToHomepage = useCallback((event?: MouseEvent<HTMLElement>) => {
    if (typeof window !== "undefined") {
      const navigationTarget = event ? openModifiedClickTarget(event) : null
      navigateToResolvedUrl(`${buildAnalysisRescanUrl({
        analysisId: savedAnalysisId,
        mode: initial.mode,
      })}#analysis-card`, navigationTarget)
    }
  }, [initial.mode, savedAnalysisId])

  // Conversion-action click on the saved-result upgrade card. Mirrors the
  // homepage handler in use-homepage-analysis but tags entry_path so the two
  // surfaces read separately in the funnel.
  // ctaLocation is supplied per render site: this one handler serves both the
  // saved diagnose and the saved identify result, and without it the two are
  // indistinguishable in the funnel.
  const handlePrimaryCtaClick = useCallback((method: "oauth" | "email", ctaLocation: string) => {
    // Saved diagnosis results have no analysisResult, so carry the diagnosed
    // problem + its confidence so the CTA-click keeps a subject dimension.
    trackEvent("result_primary_cta_clicked", {
      method,
      cta_location: ctaLocation,
      entry_path: "saved_result",
      grass_type: analysisResult?.grassType,
      problem: diagnosisResult?.problems?.[0]?.name,
      confidence: analysisResult?.confidence ?? diagnosisResult?.confidence,
      analysis_id: savedAnalysisId ?? undefined,
    })
  }, [analysisResult?.grassType, analysisResult?.confidence, diagnosisResult, savedAnalysisId])

  // Identify → diagnose reroute for saved weed/other-plant results. Without
  // this the saved Unknown/Other page is a dead end for the "Likely Crabgrass,
  // not turf" outcome (the reroute card in ResultsDisplay only renders when
  // this handler is passed). Mirrors handleSavedDiagnosisToIdentify below:
  // hop to the homepage resume lane with the same photo + ZIP in diagnose
  // mode, one tap from re-running the analysis.
  const diagnoseParams = new URLSearchParams({ resume: "1", mode: "diagnose" })
  if (uploadedImageUrl) diagnoseParams.set("imageUrl", uploadedImageUrl)
  if (zipCode) diagnoseParams.set("zipCode", zipCode)
  const savedIdentifyToDiagnoseHref = `/?${diagnoseParams.toString()}`

  const handleSavedIdentifyToDiagnose = useCallback((event?: MouseEvent<HTMLElement>) => {
    if (event && isModifiedNavigationClick(event)) return
    setAnalysisMode("diagnose")
  }, [setAnalysisMode])

  const identifyParams = new URLSearchParams({ resume: "1" })
  if (uploadedImageUrl) identifyParams.set("imageUrl", uploadedImageUrl)
  if (zipCode) identifyParams.set("zipCode", zipCode)
  const savedDiagnosisToIdentifyHref = `/?${identifyParams.toString()}`

  // Refinable saved results (abstained / low-confidence) hop back into the
  // homepage refinement lane with photo, ZIP, and lineage token, so the
  // additive re-run stays quota-exempt for whoever holds this bearer link.
  // Without the photo or token the hop cannot resume, so the CTA falls back
  // to the plain retake offer by leaving the handler undefined.
  const canOfferSavedRefinement = Boolean(uploadedImageUrl && initial.resultAccessToken)
  // The hop is a full navigation, so the result and its CTA stay mounted and
  // clickable until the new document paints. Without this latch a second tap
  // re-navigates and double-counts `refinement_started` against an impression
  // denominator that is deduped to one.
  const [savedRefinePending, setSavedRefinePending] = useState(false)
  const handleSavedRefine = useCallback((reason?: RefinementTrigger) => {
    setSavedRefinePending(true)
    trackEvent("refinement_started", {
      ...analysisFunnelEnvelope({
        funnelId,
        attemptId,
        analysisId: savedAnalysisId,
        mode: initial.mode,
        source: "saved_result_refinement",
        authState: user ? "authenticated" : "anonymous",
        actualDestination: "homepage_refinement_lane",
      }),
      mode: initial.mode,
      trigger: reason ?? null,
      auth_state: user ? "authenticated" : "anonymous",
      entry_path: "saved_result",
      outcome: "opened",
      platform: "web",
    })
    navigateToResolvedUrl(buildAnalysisRescanUrl({
      analysisId: savedAnalysisId,
      mode: initial.mode,
      resumeImageUrl: uploadedImageUrl,
      zipCode,
      sourceResultAccessToken: initial.resultAccessToken,
      refinementTrigger: reason ?? null,
    }), null)
  }, [attemptId, funnelId, initial.mode, initial.resultAccessToken, savedAnalysisId, uploadedImageUrl, user, zipCode])

  const handleSavedRefinementGateShown = useCallback((reason: RefinementTrigger) => {
    trackEvent("refinement_gate_shown", {
      ...analysisFunnelEnvelope({
        funnelId,
        attemptId,
        analysisId: savedAnalysisId,
        mode: initial.mode,
        source: "saved_result_refinement",
        authState: user ? "authenticated" : "anonymous",
        actualDestination: user ? "homepage_refinement_lane" : "refinement_signin_gate",
      }),
      mode: initial.mode,
      trigger: reason,
      auth_state: user ? "authenticated" : "anonymous",
      entry_path: "saved_result",
      platform: "web",
    })
  }, [attemptId, funnelId, initial.mode, savedAnalysisId, user])

  const handleSavedDiagnosisToIdentify = useCallback((event?: MouseEvent<HTMLElement>) => {
    if (event && isModifiedNavigationClick(event)) return
    setAnalysisMode("identify")
    clearDiagnosisResult()
  }, [clearDiagnosisResult, setAnalysisMode])

  return (
    <ResultInitialContext.Provider value={initial}>
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-emerald-50 pt-2 md:pt-6 pb-12">
      <ResultAttributionTracker
        resultId={resultId}
        resultType={initial.mode === "diagnose" ? "diagnosis" : "identify"}
      />

      {isFeatured && editorNote && (
        <div className="max-w-3xl mx-auto px-4 mb-4">
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-green-700 mb-1">
              Editor&apos;s note
            </p>
            <p className="text-sm text-gray-800 leading-relaxed">{editorNote}</p>
          </div>
        </div>
      )}

      {recoveredWeed ? (
        <WeedResultPresentation
          result={recoveredWeed.result}
          analysisId={recoveredWeed.analysisId}
          accessToken={recoveredWeed.resultAccessToken}
          funnelId={funnelId ?? `web_${recoveredWeed.analysisId}`}
          attemptId={recoveredWeed.attemptId}
          entry="identify_recovery"
          zipCode={zipCode}
        />
      ) : diagnosisResult ? (
        <DiagnosisResults
          subscriptionLoading={subscriptionLoading}
          isPremium={authIsPremium || Boolean(scanUsage?.isPremium)}
          result={diagnosisResult}
          uploadedImage={uploadedImage || initial.imageUrl || ""}
          zipCode={zipCode}
          savedAnalysisId={savedAnalysisId}
          funnelId={funnelId}
          attemptId={attemptId}
          originLawnId={originLawnId}
          selectedLawnId={selectedLawnId}
          zipMatch={null}
          onReset={handleResetToHomepage}
          onSwitchToIdentify={handleSavedDiagnosisToIdentify}
          switchToIdentifyHref={savedDiagnosisToIdentifyHref}
          onRefineWithSupportingPhoto={canOfferSavedRefinement ? handleSavedRefine : undefined}
          refinementLocked={!user}
          refinementPending={savedRefinePending}
          onRefinementGateShown={canOfferSavedRefinement ? handleSavedRefinementGateShown : undefined}
          user={user}
          authLoading={authLoading}
          redirectUrl={oauthResumeUrl}
          onBeforeOAuth={persistDiagnosisForOAuth}
          carePlanEmail={carePlanEmail}
          carePlanEmailError={carePlanEmailError}
          emailSuggestion={emailSuggestion}
          isSubmittingCarePlan={isSubmittingCarePlan}
          emailGateCompleted={emailGateCompleted}
          timelineRedirectUrl={timelineRedirectUrl}
          onEmailChange={handleCarePlanEmailChange}
          onEmailKeyPress={handleCarePlanEmailKeyPress}
          onEmailSubmit={handleCarePlanSubmit}
          onAcceptEmailSuggestion={handleAcceptEmailSuggestion}
          onDismissEmailSuggestion={handleDismissEmailSuggestion}
          onSeeCarePlan={navigateToCompletedTimeline}
          onSaveAndViewTimeline={handleSaveAndViewTimeline}
          onViewTimeline={handleViewTimeline}
          onPrimaryCtaClick={(method) => handlePrimaryCtaClick(method, "saved_result_diagnose")}
          entryPath="saved_result"
        />
      ) : analysisResult ? (
        <ResultsDisplay
          authIsPremium={authIsPremium}
          subscriptionLoading={subscriptionLoading}
          analysisResult={analysisResult}
          uploadedImage={uploadedImage || initial.imageUrl || ""}
          zipCode={zipCode}
          savedAnalysisId={savedAnalysisId}
          funnelId={funnelId}
          attemptId={attemptId}
          appCtaPlacement="saved_result"
          user={user}
          carePlanEmail={carePlanEmail}
          emailSuggestion={emailSuggestion}
          isSubmittingCarePlan={isSubmittingCarePlan}
          emailGateCompleted={emailGateCompleted}
          onSeeCarePlan={navigateToCompletedTimeline}
          carePlanHref={completedTimelineUrl ?? undefined}
          timelineRedirectUrl={timelineRedirectUrl}
          oauthFallbackRedirectUrl={oauthResumeUrl}
          onReset={handleResetToHomepage}
          onEmailChange={handleCarePlanEmailChange}
          onEmailKeyPress={handleCarePlanEmailKeyPress}
          onEmailSubmit={handleCarePlanSubmit}
          onPrimaryCtaClick={(method) => handlePrimaryCtaClick(method, "saved_result_identify")}
          onAcceptEmailSuggestion={handleAcceptEmailSuggestion}
          onDismissEmailSuggestion={handleDismissEmailSuggestion}
          onSaveAndViewTimeline={handleSaveAndViewTimeline}
          onViewTimeline={handleViewTimeline}
          onSelectAlternative={selectAlternative}
          onRefineWithSupportingPhoto={canOfferSavedRefinement ? handleSavedRefine : undefined}
          refinementLocked={!user}
          refinementPending={savedRefinePending}
          onRefinementGateShown={canOfferSavedRefinement ? handleSavedRefinementGateShown : undefined}
          onDiagnoseThisPhoto={handleSavedIdentifyToDiagnose}
          diagnoseThisPhotoHref={savedIdentifyToDiagnoseHref}
          isWeedRecoveryAvailable={weedRecoveryAvailable}
          isWeedRecoveryLoading={isRecoveringWeed}
          weedRecoveryFailed={weedRecoveryFailed}
          weedRecoveryFunnelId={funnelId ?? `web_${savedAnalysisId}`}
          weedRecoveryCohort={weedCapability.cohort}
          onIdentifyWeedThisPhoto={weedRecoveryAvailable
            ? () => { void recoverIdentifyAsWeed() }
            : undefined}
          returnedFromGuide={returnedFromGuide}
          onDismissReturnedFromGuide={dismissReturnedFromGuide}
          returnVisit={
            // Eligibility: unowned rows only ("still
            // unsaved" must be true), not editorial featured pages, and never
            // while auth is still resolving on an OAuth return — user is
            // transiently null then and the treatment would flash + mistrack.
            visitCount >= 2 && isAnonRow && !isFeatured && !authLoading
              ? { visitCount, expiresInDays: anonExpiresInDays ?? null }
              : null
          }
          belowGridSlot={weekStrip ? <SavedResultWeekStrip strip={weekStrip} /> : undefined}
          weekAhead={weekAhead}
          weekAheadLocationLabel={weekAheadLocationLabel}
        />
      ) : null}

      {canDelete && (
        <div className="mx-auto mt-8 max-w-3xl px-4 text-center">
          <DeleteButton resultId={resultId} />
        </div>
      )}

    </div>
    </ResultInitialContext.Provider>
  )
}
